import React, { createContext, useContext, useState, useEffect } from 'react';
import { buildDescriptionWithCategoryFirst } from '../utils/serviceCartDescription';

const CartContext = createContext();

/** Stable cart id for the diagnostic line — avoids colliding with catalog service ids (e.g. id 1). */
export const QUOTE_DIAGNOSTIC_CART_ID = '__quote_diagnostic__';

// Session storage key for cart data
const CART_STORAGE_KEY = 'h-autorepair-cart-data';

// Helper functions for session storage
const saveCartToSessionStorage = (data) => {
  try {
    sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
  }
};

const loadCartFromSessionStorage = () => {
  try {
    const stored = sessionStorage.getItem(CART_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    return [];
  }
};

const clearCartSessionStorage = () => {
  try {
    sessionStorage.removeItem(CART_STORAGE_KEY);
  } catch (error) {
  }
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Initialize with data from session storage
  const [cartItems, setCartItems] = useState(() => loadCartFromSessionStorage());

  // Save to session storage whenever cartItems changes
  useEffect(() => {
    saveCartToSessionStorage(cartItems);
  }, [cartItems]);

  const addToCart = (item) => {
    // Check if item is already in cart
    const existingItem = cartItems.find(cartItem => cartItem.id === item.id);
    if (!existingItem) {
      setCartItems(prev => [...prev, item]);
    }
  };

  const removeFromCart = (itemId) => {
    setCartItems(prev => prev.filter(item => item.id !== itemId));
  };

  /** Remove modal diagnostic line only (not car-won't-start quote line). */
  const removeDiagnosticFromCart = () => {
    setCartItems(prev => prev.filter(item => item.type !== 'diagnostic' && !(item.name && String(item.name).toLowerCase() === 'diagnostic')));
  };

  /** Remove all diagnostic-style selections (modal diagnostic + car won't start line). */
  const clearDiagnosticStyleSelectionsFromCart = () => {
    setCartItems(prev =>
      prev.filter(item => item.type !== 'diagnostic' && !item.isCarWontStartDiagnosticLine)
    );
  };

  /** Remove one cart item (by id, or by reference if id missing). Use from drawer so last item always removable. */
  const removeItem = (cartItem) => {
    if (!cartItem) return;
    if (cartItem.type === 'diagnostic' || (cartItem.name && String(cartItem.name).toLowerCase() === 'diagnostic')) {
      removeDiagnosticFromCart();
      return;
    }
    if (cartItem.id != null) {
      removeFromCart(cartItem.id);
      return;
    }
    setCartItems(prev => prev.filter(i => i !== cartItem));
  };

  const clearCart = () => {
    setCartItems([]);
    clearCartSessionStorage();
  };

  const addDiagnosticToCart = (diagnosticHistory, selectedCategory, optionalUserNotes = '') => {
    const history = Array.isArray(diagnosticHistory) ? diagnosticHistory : [];
    const categoryName = selectedCategory?.name || 'Diagnostic';
    const qaLines = history
      .filter(h => h && h.questionText && h.answerText)
      .map(h => `${h.questionText}\n${h.answerText}`)
      .join('\n\n');

    const extra = String(optionalUserNotes || '').trim();
    const bodyParts = [];
    if (qaLines) {
      bodyParts.push(`Questions & Answers\n${qaLines}`);
    }
    if (extra) {
      bodyParts.push(extra);
    }
    const body = bodyParts.join('\n\n').trim();
    const diagnosticDescription = buildDescriptionWithCategoryFirst(categoryName, body);

    const diagnosticItem = {
      id: QUOTE_DIAGNOSTIC_CART_ID,
      name: 'Diagnostic',
      description: diagnosticDescription,
      /** No placeholder price — diagnostic fee is set by the shop / quote, not here. */
      price: null,
      type: 'diagnostic',
      category: selectedCategory,
      diagnosticCategoryId: selectedCategory?.id,
    };

    // Replace existing diagnostic or add new one (so updated category/notes always apply)
    const existingDiagnostic = cartItems.find(item => item.type === 'diagnostic');
    setCartItems(prev => {
      if (existingDiagnostic) {
        return prev.map(item => (item.type === 'diagnostic' ? diagnosticItem : item));
      }
      return [...prev, diagnosticItem];
    });
  };

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      if (item.type === 'diagnostic') return total;
      return total + (Number(item.price) || 0);
    }, 0);
  };

  // Load cart items from QuoteContext services (for sync)
  const loadCartFromQuoteServices = (services) => {
    if (services && Array.isArray(services)) {
      setCartItems(services);
    }
  };

  // Force reload from session storage (useful for debugging)
  const reloadCartFromStorage = () => {
    const storedData = loadCartFromSessionStorage();
    setCartItems(storedData);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    removeDiagnosticFromCart,
    clearDiagnosticStyleSelectionsFromCart,
    removeItem,
    clearCart,
    addDiagnosticToCart,
    calculateTotal,
    loadCartFromQuoteServices,
    reloadCartFromStorage
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}; 